// Migrates an anonymous (no-login) session's local library into a freshly created account.
//
// When a user with anon data clicks "Sign up", Signup.tsx sets the `anon-migrate` flag (an
// `anon-` key, so logOut() preserves it through the signup flow). Once the new account is
// enabled, App.tsx calls migrateAnonToAccount(): it pushes the local subscriptions, read
// markers, folders, stars and preferences to the server with the normal account APIs, then
// clears the local anon data. The caller reloads so the migrated account loads fresh from
// GET /api/feeds. Logging IN (vs. signing up) does NOT set the flag — there the anon data is
// kept dormant so it can be restored on logout (see logOut() in auth.ts).

import { AuthData, authenticatedRequest } from './auth';
import { subscribeFeed, patchReadMarkers, updateFolders, patchStarred, patchReadLater } from './api';
import { clearAnonData } from './feed-engine';
import { ReadMarkerEngine, SerializedFeedMarker } from './feed-engine/read-marker-engine';
import { PostMarker, RestFolderUpdate, RestReadMarkerPatch, RestUser, RestUserFeed, UserPreferences } from '../schema-copy/api-schema';
import { getPlanLimits } from '../schema-copy/userPlans';

const ANON_MIGRATE_KEY = 'anon-migrate';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

export function isAnonMigrationPending(): boolean {
  return localStorage.getItem(ANON_MIGRATE_KEY) == '1';
}

// Signing up FROM an anonymous session with feeds: flag the migration so that, once the new
// account is enabled, App.tsx transfers the local anon library (feeds, read state, folders,
// stars, prefs) into it and then deletes it. The `anon-` prefix means logOut() — called at the
// start of the signup flow — preserves the flag (and the anon-* data) rather than wiping it.
// Login does NOT set this (it keeps the anon data dormant for restore-on-logout instead).
// Must run BEFORE the signUp() call (whose logOut() wipes non-anon localStorage).
export function markAnonMigrationIfNeeded(): void {
  try {
    const feeds = JSON.parse(localStorage.getItem('anon-feeds') || '[]');
    if (Array.isArray(feeds) && feeds.length) localStorage.setItem(ANON_MIGRATE_KEY, '1');
  } catch { /* ignore malformed storage */ }
}

// Anon stores star/read-later entries as "feedId-postIndex"; the server expects "feedId/postIndex".
function toServerStarEntry(entry: string): string {
  return entry.replace(/-(\d+)$/, '/$1');
}

// Collapse a set of read post indices into the PostMarker[] (single index or [start,end] range) form.
function toRanges(readSet: Set<number>): PostMarker[] {
  const sorted = Array.from(readSet).sort((a, b) => a - b);
  const ranges: PostMarker[] = [];
  for (let i = 0; i < sorted.length;) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] == sorted[j] + 1) j++;
    ranges.push(i == j ? sorted[i] : [sorted[i], sorted[j]]);
    i = j + 1;
  }
  return ranges;
}

// Decode the anon read bitmaps into a single markAsRead patch, reusing the marker engine's own
// bitmap logic via a throwaway instance seeded with the cached feed metadata (for post counts).
function buildReadMarkerPatch(feedIds: string[]): RestReadMarkerPatch {
  const markers = readJson<SerializedFeedMarker[]>('anon-markers', []);
  if (!markers.length) return {};
  const meta = readJson<Record<string, RestUserFeed>>('anon-feed-meta', {});
  const engine = new ReadMarkerEngine({
    getAuth: () => null,
    getFeed: (id) => meta[id],
    getFeeds: () => Object.values(meta),
    broadcastMarkers: () => { /* no-op: nothing else listens to this throwaway engine */ },
  });
  engine.handleBroadcastMessage(markers);

  const patch: RestReadMarkerPatch = {};
  for (const feedId of feedIds) {
    const readSet = engine.getReadForFeed(feedId);
    if (readSet.size) patch[feedId] = { markAsRead: toRanges(readSet) };
  }
  return patch;
}

// Anon feed ids the given account is not subscribed to. Empty when there is nothing to import.
export function getAnonFeedIdsMissingFromAccount(accountFeedIds: string[]): string[] {
  const account = new Set(accountFeedIds);
  return readJson<string[]>('anon-feeds', []).filter(id => !account.has(id));
}

// The cached anon feed metadata, for displaying feed titles without a server call.
export function getAnonFeedMeta(): Record<string, RestUserFeed> {
  return readJson<Record<string, RestUserFeed>>('anon-feed-meta', {});
}

// Where to send a user who just logged INTO AN EXISTING account: the import page when the anon
// storage has feeds the account lacks AND the plan quota has room for at least one more, else
// undefined (caller proceeds to its normal target).
export function getAnonImportPath(user: RestUser, returnTo: string): string | undefined {
  if (!getAnonFeedIdsMissingFromAccount(user.feeds).length) return undefined;
  if (user.feeds.length >= getPlanLimits(user.userPlan).maxFeeds) return undefined;
  const target = returnTo.startsWith('/a/import-feeds') ? '/a' : returnTo;
  return `/a/import-feeds?return=${encodeURIComponent(target)}`;
}

// Subscribe the account to the given anon feeds and push their local read markers. Best-effort
// per subscribe (e.g. a feed that went inactive); throws only if the marker patch fails. Does
// NOT touch the local anon data — that's the caller's decision.
export async function importAnonFeedsToAccount(auth: AuthData, feedIds: string[]): Promise<void> {
  // No bulk subscribe endpoint, so one-by-one (≤ MAX_ANON_FEEDS).
  for (const id of feedIds) {
    try {
      await subscribeFeed(auth, id);
    } catch (e) {
      console.error('anon import: subscribe failed', id, e);
    }
  }

  const patch = buildReadMarkerPatch(feedIds);
  if (Object.keys(patch).length)
    await patchReadMarkers(auth, patch);
}

// Transfer the anon library into the (newly created) account. Best-effort per feed; throws if a
// bulk push fails so the caller keeps the local data + flag and retries on the next boot. On
// success the local anon data is cleared (incl. the `anon-migrate` flag, via clearAnonData()).
export async function migrateAnonToAccount(auth: AuthData): Promise<void> {
  const feedIds = readJson<string[]>('anon-feeds', []);

  // 1+2. Subscriptions and read markers (shared with the post-login import page).
  await importAnonFeedsToAccount(auth, feedIds);

  // 3. Folders (full structure).
  const folders = readJson<RestFolderUpdate>('anon-folders', {});
  if (Object.keys(folders).length)
    await updateFolders(auth, folders);

  // 4. Starred / read-later.
  const starred = readJson<string[]>('anon-starred', []).map(toServerStarEntry);
  if (starred.length) await patchStarred(auth, { added: starred });
  const readLater = readJson<string[]>('anon-readlater', []).map(toServerStarEntry);
  if (readLater.length) await patchReadLater(auth, { added: readLater });

  // 5. Preferences (full blob; the server merges per-field). Mirrors usePutPreferences().
  const prefs = readJson<UserPreferences | {}>('anon-preferences', {});
  if (Object.keys(prefs).length)
    await authenticatedRequest<unknown>(auth, 'PUT', 'api', '/api/preferences', false, prefs);

  clearAnonData();
}
